import { orderBy } from "lodash";
import { ref } from "vue";
import axios from "axios";
import Swal from "sweetalert2";

export function useGetLine() {
  // Reactive variable to hold the line data
  const line = ref({
    id: null,
    name: null,
    used_at: null,
    line_manufacturer: null,
    pump_type: null,
    pistols_layout: null,
    air_parameter_name: null,
    air_parameter_units: null,
    total_pistols: [],
    pistol_to_pistol_distance: [],
    pistols_min_velocity: null,
    pistols_max_velocity: null,
    pistols_max_movement_range: null,
    pistol_columns_distances: [],
    pistol_columns_vertical_offsets: [],
    powder_recycling_efficiency_percentage: null,
    pistols_max_acceleration: null,
  });

  const getLine = async lineId => {
    try {
      let line_response = await axios.get(`/api/v1/fp/line/${lineId}`);

      line_response.data.pistols_max_movement_range = line_response.data.pistols_max_movement_range * 100;

      line.value = line_response.data;
    } catch (error) {
      console.error(error);
    }
  };

  return {
    line,
    getLine,
  };
}

export function useGetPowders() {
  // Reactive variable to hold the line data
  const powders = ref([]);

  const getPowders = async lineId => {
    try {
      if (lineId == undefined || lineId == null || lineId == "") {
        const response = await axios.get("/api/v1/blueprint/powder/");
        powders.value = JSON.parse(JSON.stringify(response.data));
      } else {
        const response = await axios.get("/api/v1/blueprint/powdersbyline/" + lineId + "/");
        powders.value = JSON.parse(JSON.stringify(response.data));
      }
    } catch (error) {
      console.error(error);
    }
  };

  return {
    powders,
    getPowders,
  };
}

export function useGetCalibratedPowderModels() {
  // Reactive variable to hold the line data
  const calibrated_powdermodels = ref([]);

  const getCalibratedPowderModels = async lineId => {
    try {
      const response = await axios.get("/api/v1/fp/powdermodels/lineid/" + lineId);
      calibrated_powdermodels.value = JSON.parse(JSON.stringify(response.data));
    } catch (error) {
      console.error(error);
    }
  };

  return {
    calibrated_powdermodels,
    getCalibratedPowderModels,
  };
}

export function numericPositiveOnly(event) {
  let key = event.key;
  if (
    [
      "Delete",
      "Backspace",
      "Tab",
      "Enter",
      "ArrowUp",
      "ArrowRight",
      "ArrowDown",
      "ArrowLeft",
      ".",
      "Shift",
      "Control",
      ",",
    ].includes(key) ||
    ((event.ctrlKey || event.metaKey) && ["a", "c", "v", "x", "z", "y"].includes(key.toLowerCase()))
  ) {
    return;
  }
  if (!/\d/.test(key)) {
    event.preventDefault();
  }
}

export function useGetVisit() {
  const visit = ref({
    id: null,
    benchmarkstage: {},
    healthcheckstage: {},
    line: null,
    thicknessadjustmentstage: {},
    gunsequalizationstage: {},
    reciprocatorstage: {},
    spraypatternstage: {
      selected_powder_model: null,
    },
  });

  const getVisit = async visitId => {
    try {
      const response = await axios.get("/api/v1/blueprint/visit/" + visitId);
      visit.value = response.data;
    } catch (error) {
      console.error(error);
    }
  };

  return {
    visit,
    getVisit,
  };
}

export function useGetBenchmark() {
  const benchmark_stage_data = ref({
    minimum_target_thickness: null,
    maximum_target_thickness: null,
    reciprocator_period: null,
    guns_movement_range: null,
    line_speed: null,
    coated_height: null,
    nozzle_angle: null,
    gun_settings: null,
    thickness_measurements: Array(5).fill(null),
    powder: null,
  });

  const getBenchmarkData = async (visitId, totalPistols) => {
    try {
      let response = await axios.get(`/api/v1/blueprint/benchmarkstage/` + visitId);
      if (response.data !== null) {
        benchmark_stage_data.value.minimum_target_thickness = response.data.minimum_target_thickness;
        benchmark_stage_data.value.maximum_target_thickness = response.data.maximum_target_thickness;
        benchmark_stage_data.value.reciprocator_period = response.data.reciprocator_period;
        benchmark_stage_data.value.guns_movement_range = response.data.guns_movement_range;
        benchmark_stage_data.value.line_speed = response.data.line_speed;
        benchmark_stage_data.value.coated_height = response.data.coated_height;
        benchmark_stage_data.value.nozzle_angle = response.data.nozzle_angle;
        benchmark_stage_data.value.thickness_measurements = response.data.thickness_measurements;
        benchmark_stage_data.value.powder = response.data.powder;

        if (
          benchmark_stage_data.value.thickness_measurements === null ||
          benchmark_stage_data.value.thickness_measurements === ""
        ) {
          benchmark_stage_data.value.thickness_measurements = Array(5).fill(null);
        } else if (benchmark_stage_data.value.thickness_measurements.length < 5) {
          benchmark_stage_data.value.thickness_measurements = benchmark_stage_data.value.thickness_measurements.concat(
            Array(5 - benchmark_stage_data.value.thickness_measurements.length).fill(null),
          );
        }
        if (response.data.gun_settings === null || response.data.gun_settings.length < 2) {
          benchmark_stage_data.value.gun_settings = Array.from({ length: totalPistols }, () => ({
            air: null,
            amps: null,
            powder: null,
            voltage: null,
            distance: null,
          }));
        } else {
          benchmark_stage_data.value.gun_settings = response.data.gun_settings;
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return {
    benchmark_stage_data,
    getBenchmarkData,
  };
}

export function getThicknessMeasurementsMean(thickness_measurements) {
  if (!Array.isArray(thickness_measurements) || thickness_measurements.length === 0) {
    return 0;
  }

  let filtered_thickness_measurements = thickness_measurements.filter(m => m !== null && m !== "");

  if (filtered_thickness_measurements.length === 0) {
    return 0;
  }

  let mean = parseFloat(
    (filtered_thickness_measurements.reduce((a, b) => a + b, 0) / filtered_thickness_measurements.length).toFixed(1),
  );
  return mean;
}

export function getThicknessMeasurementsStd(thickness_measurements) {
  const mean = getThicknessMeasurementsMean(thickness_measurements);

  let filtered_thickness_measurements = thickness_measurements.filter(m => m !== null && m !== "");

  return parseFloat(
    Math.sqrt(
      filtered_thickness_measurements.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) /
        filtered_thickness_measurements.length,
    ).toFixed(1),
  );
}

export function measurementsMin(thickness_measurements) {
  return parseFloat(Math.min(...thickness_measurements.filter(m => m !== null && m !== "")).toFixed(1));
}

export function measurementsMax(thickness_measurements) {
  if (!Array.isArray(thickness_measurements) || thickness_measurements.length === 0) {
    return 0;
  }
  return parseFloat(Math.max(...thickness_measurements.filter(m => m !== null && m !== "")).toFixed(1));
}

export function showStatistics(measurements) {
  if (measurements === null || measurements === undefined) {
    return false;
  }

  if (measurements.every(m => m === null || m === "")) {
    return false;
  }

  return getThicknessMeasurementsMean(measurements) > 0 && getThicknessMeasurementsStd(measurements) > 0;
}

export function getStdDifferencePercentage(measurement, benchmarkMeasurement) {
  if (measurement === null || benchmarkMeasurement === null) {
    return null;
  }
  if (measurement.length === 0 || benchmarkMeasurement.length === 0) {
    return null;
  }
  if (measurement.every(m => m === null || m === "") || benchmarkMeasurement.every(m => m === null || m === "")) {
    return null;
  }

  const measurementStd = getThicknessMeasurementsStd(measurement);
  const benchmarkStd = getThicknessMeasurementsStd(benchmarkMeasurement);
  if (benchmarkStd < measurementStd) {
    return null;
  }
  const percent = (((measurementStd - benchmarkStd) / benchmarkStd) * 100).toFixed(1);
  return percent;
}

export function numericOnly(event) {
  let key = event.key;
  if (
    [
      "Delete",
      "Backspace",
      "Tab",
      "Enter",
      "ArrowUp",
      "ArrowRight",
      "ArrowDown",
      "ArrowLeft",
      ".",
      "Shift",
      "Control",
      ",",
      "-",
    ].includes(key) ||
    ((event.ctrlKey || event.metaKey) && ["a", "c", "v", "x", "z", "y"].includes(key.toLowerCase()))
  ) {
    return;
  }
  if (!/\d/.test(key)) {
    event.preventDefault();
  }
}

export function generateMeasurementList(powderoutput_measurements) {
  const results = [];

  powderoutput_measurements.forEach(gun => {
    const validPairs = gun.gun_measurements.filter(pair => {
      return pair.setting !== "" && pair.weight !== "";
    });

    const emptyBagWeight = gun.empty_bag_weight == null || gun.empty_bag_weight === "" ? 0 : gun.empty_bag_weight;

    const result = {
      powderAmount: validPairs.map(pair => parseFloat(pair.setting)),
      emptyBagWeight: validPairs.map(() => emptyBagWeight),
      fullBagWeight: validPairs.map(pair => parseFloat(pair.weight)),
    };

    if (result.powderAmount.length > 0 || result.fullBagWeight.length > 0) {
      results.push(result);
    }
  });

  return results;
}

export function OrderByName(array) {
  return orderBy(array, [array => array.name.toLowerCase()], ["asc"]);
}

export function OrderByDateUpdated(array) {
  return orderBy(array, ["updated_at", "created_at"], ["desc"]);
}

export function OrderByLineUsedDate(array) {
  return orderBy(array, ["used_at", "created_at"], ["desc"]);
}

export function OrderByDateCreated(array) {
  return orderBy(array, ["created_at"], ["desc"]);
}

export function validateEmail(email) {
  const regex = /^\S+@\S+\.\S+$/;
  return regex.test(email);
}

export function getTimestamp() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${month}/${day}/${year} ${hours}:${minutes}`;
}

export function formatIsoDateToLocalString(isoDateString) {
  const date = new Date(isoDateString);
  // Format as MM/DD/YYYY, HH:MM AM/PM (en-US)
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
}

export function gunNamesExist(gun_names) {
  if (
    gun_names == null ||
    gun_names == undefined ||
    gun_names.length == 0 ||
    gun_names.every(name => name === "" || name === null)
  ) {
    return false;
  }
  return true;
}

export function autoFillInputColumn(gun_array, gun_index, property_key) {
  if (gun_index !== 0) {
    return gun_array;
  }

  const inputValue = gun_array[gun_index][property_key];
  const emptyInputValue = inputValue === "" || inputValue === null;

  const isColumnEmpty = gun_array.slice(1).every(gun => gun[property_key] === "" || gun[property_key] === null);

  if (!emptyInputValue && isColumnEmpty) {
    gun_array.forEach(gun => {
      gun[property_key] = inputValue;
    });
    return gun_array;
  }

  if (emptyInputValue && !isColumnEmpty) {
    const allValuesAreTheSame = gun_array
      .slice(1)
      .every(gun => gun[property_key] === gun_array[gun_index + 1][property_key]);
    if (allValuesAreTheSame) {
      gun_array.forEach(gun => {
        gun[property_key] = "";
      });
    }
  }
  return gun_array;
}

export function dateToString(date, showTime = false) {
  const date_object = new Date(date);
  if (this.$store.state.user_data.unit_system == "metric") {
    return showTime
      ? date_object.toLocaleString("en-GB", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : date_object.toLocaleDateString("en-GB");
  } else if (this.$store.state.user_data.unit_system == "imperial") {
    return showTime
      ? date_object.toLocaleString("en-US", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : date_object.toLocaleDateString("en-US");
  } else {
    return null;
  }
}

export function roundPowderAmountParameters(powder_amount_parameters) {
  return powder_amount_parameters.map(param => {
    return parseFloat(param.toFixed(1));
  });
}

export function checkLastRow(thickness_measurements) {
  while (thickness_measurements.length < 5) {
    thickness_measurements.push("");
  }

  if (thickness_measurements.every(m => m !== null && m !== "" && m !== 0)) {
    thickness_measurements.push("");
  } else if (
    (thickness_measurements[thickness_measurements.length - 1] == null ||
      thickness_measurements[thickness_measurements.length - 1] == "") &&
    (thickness_measurements[thickness_measurements.length - 2] == null ||
      thickness_measurements[thickness_measurements.length - 2] == "") &&
    thickness_measurements.length > 5
  ) {
    thickness_measurements.pop();
  }
  return thickness_measurements;
}

export async function markVisitAsFinalized(visit_id, finalized_at_stage) {
  let result = await Swal.fire({
    title: `Are you sure you want to mark the Visit as finished?`,
    text: "The visit will no longer be editable. Are you sure?",
    showCancelButton: true,
    confirmButtonText: "Yes, mark as finished",
    cancelButtonText: "No, cancel",
    reverseButtons: true,
    customClass: {
      confirmButton: "btn bg-gradient-warning", // Added margin-end class for spacing
      cancelButton: "btn bg-gradient-danger me-2",
    },
    buttonsStyling: false,
  });
  if (result.isConfirmed) {
    await axios.patch(`/api/v1/blueprint/visit/${visit_id}/`, {
      finalized_at_stage: finalized_at_stage,
    });
  }
}

export function cleanThicknessMeasurementsArray(array) {
  return array.filter(m => m !== null && m !== "");
}

export function nozzleAngleTooltipImage(s3_storage_base_URL) {
  let url = s3_storage_base_URL + "angle-0-200x200.jpg";
  return `<img src="${url}" alt="Tooltip Image">`;
}

export function getCombinedGunNames(reciprocator_side_A, reciprocator_side_B) {
  let gun_names = [];

  if (reciprocator_side_A == null || reciprocator_side_B == null) {
    return gun_names;
  }

  if (reciprocator_side_A.gun_names && reciprocator_side_B.gun_names) {
    gun_names = reciprocator_side_A.gun_names.concat(reciprocator_side_B.gun_names);
  } else {
    let booth_A_guns = reciprocator_side_A.total_pistols.reduce((a, b) => a + b, 0);
    let booth_B_guns = reciprocator_side_B.total_pistols.reduce((a, b) => a + b, 0);
    const gun_names_reciprocator_A = Array.from({ length: booth_A_guns }, (_, index) => `${index + 1}`);
    reciprocator_side_A.gun_names = gun_names_reciprocator_A;
    const gun_names_reciprocator_B = Array.from(
      { length: booth_B_guns },
      (_, index) => `${gun_names_reciprocator_A.length + index + 1}`,
    );
    reciprocator_side_B.gun_names = gun_names_reciprocator_B;
    gun_names = gun_names_reciprocator_A.concat(gun_names_reciprocator_B);
  }
  return gun_names;
}

export function removeNullEntriesFromObject(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== null));
}

export async function getLatestPowderOutputMeasurementsByLine(lineId) {
  let response = await axios.get(`/api/v1/blueprint/getlatestpowderoutputmeasurementsbyline/${lineId}/`);
  if (!response.data || !response.data.powder_output_measurements) {
    throw new Error("Empty response data or powder_output_measurements");
  }

  const powder_output_measurements = response.data.powder_output_measurements;
  return powder_output_measurements;
}

export function isEmptyPowderOutputMeasurements(powder_output_measurements) {
  if (
    powder_output_measurements == null ||
    powder_output_measurements.length == undefined ||
    powder_output_measurements.length == 0 ||
    powder_output_measurements[0].gun_measurements == undefined ||
    powder_output_measurements[0].gun_measurements.length == undefined ||
    powder_output_measurements[0].gun_measurements.length == 0 ||
    powder_output_measurements[0].gun_measurements[0].setting == undefined ||
    powder_output_measurements[0].gun_measurements[0].weight == undefined ||
    powder_output_measurements[0].empty_bag_weight == undefined
  ) {
    return true;
  }

  const empty_powder_output_measurements = powder_output_measurements.some(gun =>
    gun.gun_measurements.some(
      pair => pair.setting == null || pair.setting == "" || pair.weight == null || pair.weight == "",
    ),
  );
  const empty_bag_weight_measurements = powder_output_measurements.some(
    gun => gun.empty_bag_weight == null || gun.empty_bag_weight == "",
  );

  return empty_powder_output_measurements || empty_bag_weight_measurements;
}

export function getTestPowderOutputMeasurements() {
  const test_powder_output_measurements = [
    {
      empty_bag_weight: 33,
      gun_measurements: [
        {
          weight: 64,
          setting: 3,
        },
        {
          weight: 115,
          setting: 4,
        },
        {
          weight: 198,
          setting: 5,
        },
      ],
    },
    {
      empty_bag_weight: 36,
      gun_measurements: [
        {
          weight: 73,
          setting: 3,
        },
        {
          weight: 119,
          setting: 4,
        },
        {
          weight: 192,
          setting: 5,
        },
      ],
    },
    {
      empty_bag_weight: 31,
      gun_measurements: [
        {
          weight: 55,
          setting: 3,
        },
        {
          weight: 110,
          setting: 4,
        },
        {
          weight: 204,
          setting: 5,
        },
      ],
    },
    {
      empty_bag_weight: 33,
      gun_measurements: [
        {
          weight: 68,
          setting: 3,
        },
        {
          weight: 118,
          setting: 4,
        },
        {
          weight: 202,
          setting: 5,
        },
      ],
    },
  ];
  return test_powder_output_measurements;
}
